import React, { useState } from "react";
import { Loader2, NotebookPen, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ApplicationNotesDict, FlatApplicationNote, ApplicationUserNotes } from "@/types/borrower";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useAddBorrowerNoteMutation, useRemoveBorrowerNoteMutation } from "@/hooks/queries";

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function flattenAndSort(notes: ApplicationNotesDict): FlatApplicationNote[] {
  return Object.entries(notes)
    .flatMap(([userId, userData]) =>
      userData.notes.map((n) => ({
        ...n,
        userId,
        userName: userData.userName,
        userRole: userData.userRole,
      }))
    )
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
}

const ROLE_VARIANT: Record<ApplicationUserNotes["userRole"], "default" | "secondary" | "outline"> =
  {
    admin: "default",
    underwriter: "secondary",
    broker: "outline",
    system: "outline",
  };

// ─── Component ───────────────────────────────────────────────────────────────

interface NotesDialogProps {
  open: boolean;
  onClose: () => void;
  borrowerId: number;
  notes: ApplicationNotesDict;
}

export const NotesDialog: React.FC<NotesDialogProps> = ({ open, onClose, borrowerId, notes }) => {
  const [newNoteText, setNewNoteText] = useState("");
  const { user } = useCurrentUser();

  const addMutation = useAddBorrowerNoteMutation(borrowerId);
  const removeMutation = useRemoveBorrowerNoteMutation(borrowerId);

  const thread = flattenAndSort(notes);

  const canDeleteAny = user?.role === "admin";
  const canDelete = (note: FlatApplicationNote) => canDeleteAny || note.userId === String(user?.id);

  const handleAdd = () => {
    const trimmed = newNoteText.trim();
    if (!trimmed) return;
    addMutation.mutate(trimmed, { onSuccess: () => setNewNoteText("") });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[80vh] max-w-xl flex-col [&>button.absolute]:hidden">
        <DialogHeader className="space-y-0 text-left">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="flex items-center gap-2 text-xl font-bold">
              <NotebookPen className="h-4 w-4" />
              Notes
            </DialogTitle>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Notes thread */}
        <div className="flex-1 space-y-3 overflow-y-auto py-2">
          {thread.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No notes yet.</p>
          ) : (
            thread.map((note) => (
              <div key={note.id} className="rounded-lg border bg-muted/30 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    {note.userRole !== "system" ? (
                      <>
                        <span className="truncate text-sm font-medium">{note.userName}</span>
                        <Badge variant={ROLE_VARIANT[note.userRole]} className="shrink-0 text-xs">
                          {note.userRole}
                        </Badge>
                      </>
                    ) : (
                      <span className="text-xs italic text-muted-foreground">Migrated Note</span>
                    )}
                  </div>
                  {canDelete(note) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeMutation.mutate(note.id)}
                      disabled={removeMutation.isPending}
                      aria-label="Delete note"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <p className="mt-1.5 whitespace-pre-wrap text-sm">{note.text}</p>
              </div>
            ))
          )}
        </div>

        <Separator />

        {/* Add note */}
        <div className="space-y-2 pt-2">
          <Textarea
            rows={3}
            placeholder="Add a note…"
            value={newNoteText}
            onChange={(e) => setNewNoteText(e.target.value)}
            disabled={addMutation.isPending}
            className="resize-y"
          />
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              onClick={handleAdd}
              disabled={!newNoteText.trim() || addMutation.isPending}
            >
              {addMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Note
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
